/**
 * NsectionB8Page
 * B.8.活動邊界（A6.4-FORM-METH-002 Section 8. Activity Boundary）
 * 四頁籤：(1)邊界說明-富文本 (2)基準排放-JSON CRUD (3)活動排放-JSON CRUD (4)洩漏排放-JSON CRUD
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import nsectionB8Service, { NsectionB8Detail, EmissionSource, GhgItem } from '../services/nsectionB8Service';
import { getSystemCodesByType, SystemCode } from '../services/systemCodeService';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

const RELATIONSHIP_OPTIONS = ['Controlled', 'Related to', 'Affected by'];

const NsectionB8Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProposalId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  // ========== 頁籤 ==========
  const [activeTab, setActiveTab] = useState<number>(1);

  // ========== 資料狀態 ==========
  const [data, setData] = useState<NsectionB8Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ========== 表單狀態 ==========
  const [boundaryDescription, setBoundaryDescription] = useState('');
  const [baselineEmissions, setBaselineEmissions] = useState<EmissionSource[]>([]);
  const [activityEmissions, setActivityEmissions] = useState<EmissionSource[]>([]);
  const [leakageEmissions, setLeakageEmissions] = useState<EmissionSource[]>([]);
  const [originalData, setOriginalData] = useState({
    boundary: '', baseline: [] as EmissionSource[], activity: [] as EmissionSource[], leakage: [] as EmissionSource[]
  });

  // ========== GHG 選項 ==========
  const [ghgOptions, setGhgOptions] = useState<SystemCode[]>([]);

  // ========== Modal 狀態 ==========
  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [modalSource, setModalSource] = useState('');
  const [modalGhg, setModalGhg] = useState<(GhgItem & { _selected?: boolean })[]>([]);

  // ========== 頁籤定義 ==========
  const tabs = [
    { key: 1, label: isZh ? 'B.8.1 邊界說明' : 'B.8.1 Boundary description' },
    { key: 2, label: isZh ? 'B.8.2 基準排放' : 'B.8.2 Baseline emissions' },
    { key: 3, label: isZh ? 'B.8.3 活動排放' : 'B.8.3 Activity emissions' },
    { key: 4, label: isZh ? 'B.8.4 洩漏排放' : 'B.8.4 Leakage emissions' },
  ];

  // ========== 初始化 ==========
  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('NsectionB8Page');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  // ========== 載入 GHG 選項 ==========
  useEffect(() => {
    const loadGhgOptions = async () => {
      try {
        const codes = await getSystemCodesByType('GHG');
        setGhgOptions(codes);
      } catch (err) {
        console.error('載入 GHG 選項失敗:', err);
      }
    };
    loadGhgOptions();
  }, []);

  // ========== 提案變更時載入資料 ==========
  useEffect(() => {
    if (!selectedProposalId) {
      setData(null);
      setBoundaryDescription('');
      setBaselineEmissions([]);
      setActivityEmissions([]);
      setLeakageEmissions([]);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true); setError(null);
        const result = await nsectionB8Service.getByProposal(selectedProposalId);
        if (result) {
          setData(result);
          setBoundaryDescription(result.boundary_description || '');
          setBaselineEmissions(result.baseline_emissions || []);
          setActivityEmissions(result.activity_emissions || []);
          setLeakageEmissions(result.leakage_emissions || []);
          setOriginalData({
            boundary: result.boundary_description || '',
            baseline: result.baseline_emissions || [],
            activity: result.activity_emissions || [],
            leakage: result.leakage_emissions || []
          });
        } else {
          setData(null);
          setBoundaryDescription('');
          setBaselineEmissions([]);
          setActivityEmissions([]);
          setLeakageEmissions([]);
          setOriginalData({ boundary: '', baseline: [], activity: [], leakage: [] });
        }
      } catch (err: any) {
        if (err.response?.status !== 404) {
          setError(err.response?.data?.detail || (isZh ? '載入資料失敗' : 'Failed to load data'));
        }
      } finally { setLoading(false); }
    };
    loadData();
  }, [selectedProposalId, isZh]);

  // ========== 取得目前頁籤的排放源資料 ==========
  const getCurrentEmissions = useCallback((): EmissionSource[] => {
    if (activeTab === 2) return baselineEmissions;
    if (activeTab === 3) return activityEmissions;
    return leakageEmissions;
  }, [activeTab, baselineEmissions, activityEmissions, leakageEmissions]);

  const setCurrentEmissions = useCallback((data: EmissionSource[]) => {
    if (activeTab === 2) setBaselineEmissions(data);
    else if (activeTab === 3) setActivityEmissions(data);
    else setLeakageEmissions(data);
  }, [activeTab]);

  // ========== 建立新排放源的預設 GHG ==========
  const createDefaultGhg = (): (GhgItem & { _selected?: boolean })[] => {
    return ghgOptions.map(opt => ({
      gas_id: opt.id,
      is_included: false,
      relationship: 'Controlled',
      justification: '',
      _selected: true
    }));
  };

  // ========== 取得 GHG 名稱 ==========
  const getGasName = (gasId: number): string => {
    const code = ghgOptions.find(o => o.id === gasId);
    if (!code) return String(gasId);
    return isZh ? (code.code_cname || code.code_ename || String(gasId)) : (code.code_ename || code.code_cname || String(gasId));
  };

  // ========== relationship 中文 ==========
  const getRelationshipLabel = (rel: string): string => {
    if (!isZh) return rel;
    if (rel === 'Controlled') return '受控';
    if (rel === 'Related to') return '相關';
    if (rel === 'Affected by') return '受影響';
    return rel;
  };

  // ========== Modal 操作 ==========
  const openModal = (index?: number) => {
    const emissions = getCurrentEmissions();
    if (index !== undefined && index < emissions.length) {
      const item = emissions[index];
      setEditingIndex(index);
      setModalSource(item.source);
      const allGhg = ghgOptions.map(opt => {
        const existing = item.ghg.find(g => g.gas_id === opt.id);
        if (existing) {
          return { ...existing, _selected: true };
        }
        return { gas_id: opt.id, is_included: false, relationship: 'Controlled', justification: '', _selected: false };
      });
      setModalGhg(allGhg);
    } else {
      setEditingIndex(null);
      setModalSource('');
      setModalGhg(createDefaultGhg());
    }
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditingIndex(null); };

  // ========== modalGhg 操作（避免 .map 內 inline handler 的 stale closure 問題） ==========
  const updateModalGhg = (idx: number, patch: Partial<GhgItem & { _selected?: boolean }>) => {
    setModalGhg(prev => {
      if (idx < 0 || idx >= prev.length) return prev;
      const updated = [...prev];
      updated[idx] = { ...updated[idx], ...patch };
      return updated;
    });
  };

  const handleModalSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!modalSource.trim()) {
      toast.warning(isZh ? '請輸入排放源名稱' : 'Please enter source name');
      return;
    }
    const emissions = [...getCurrentEmissions()];
    const filteredGhg: GhgItem[] = modalGhg
      .filter((g: any) => g._selected !== false)
      .map(({ gas_id, is_included, relationship, justification }) => ({ gas_id, is_included, relationship, justification }));
    const newItem: EmissionSource = { source: modalSource.trim(), ghg: filteredGhg };

    if (editingIndex !== null) {
      emissions[editingIndex] = newItem;
    } else {
      emissions.push(newItem);
    }
    setCurrentEmissions(emissions);
    closeModal();
  };

  const handleDeleteSource = async (index: number) => {
    const ok = await toast.confirm({
      title: isZh ? '刪除確認' : 'Delete Confirmation',
      message: isZh ? '確定要刪除此排放源嗎？' : 'Are you sure you want to delete this source?',
      confirmText: isZh ? '刪除' : 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    const emissions = [...getCurrentEmissions()];
    emissions.splice(index, 1);
    setCurrentEmissions(emissions);
  };

  // ========== 儲存（依照目前頁籤個別儲存） ==========
  const handleSave = async () => {
    if (!selectedProposalId) return;
    try {
      setLoading(true); setError(null);
      const updateData: any = {};
      if (activeTab === 1) updateData.boundary_description = boundaryDescription || undefined;
      else if (activeTab === 2) updateData.baseline_emissions = baselineEmissions;
      else if (activeTab === 3) updateData.activity_emissions = activityEmissions;
      else if (activeTab === 4) updateData.leakage_emissions = leakageEmissions;

      const result = await nsectionB8Service.update(selectedProposalId, updateData);
      setData(result);
      if (activeTab === 1) setOriginalData(prev => ({ ...prev, boundary: result.boundary_description || '' }));
      else if (activeTab === 2) setOriginalData(prev => ({ ...prev, baseline: result.baseline_emissions || [] }));
      else if (activeTab === 3) setOriginalData(prev => ({ ...prev, activity: result.activity_emissions || [] }));
      else if (activeTab === 4) setOriginalData(prev => ({ ...prev, leakage: result.leakage_emissions || [] }));
      toast.success(isZh ? '儲存成功' : 'Saved successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.detail || (isZh ? '儲存失敗' : 'Save failed'));
    } finally { setLoading(false); }
  };

  // ========== 重置 ==========
  const handleReset = () => {
    if (activeTab === 1) setBoundaryDescription(originalData.boundary);
    else if (activeTab === 2) setBaselineEmissions(JSON.parse(JSON.stringify(originalData.baseline)));
    else if (activeTab === 3) setActivityEmissions(JSON.parse(JSON.stringify(originalData.activity)));
    else if (activeTab === 4) setLeakageEmissions(JSON.parse(JSON.stringify(originalData.leakage)));
    setError(null);
  };

  // ========== 權限檢查 ==========
  if (permissionLoading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  if (!hasPermission('nsection_b8', 'read')) return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;
  const canEdit = hasPermission('nsection_b8', 'update');
  const currentEmissions = getCurrentEmissions();

  // ========== 渲染排放源表格 ==========
  const renderEmissionsTable = () => {
    if (currentEmissions.length === 0) {
      return (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
          {isZh ? '尚無資料，請點擊「新增」按鈕新增排放源' : 'No data yet. Click "Create" to add a source.'}
        </div>
      );
    }
    return (
      <div className="data-table-container">
        <table className="data-table">
          <thead className="table-header-dark-green">
            <tr>
              <th style={{ width: '16%' }}>{isZh ? '排放源/碳庫' : 'Source'}</th>
              <th style={{ width: '8%' }}>{isZh ? '溫室氣體' : 'GHG'}</th>
              <th style={{ width: '12%' }}>{isZh ? '是否包含' : 'Included'}</th>
              <th style={{ width: '14%' }}>{isZh ? '受控/相關/受影響' : 'Controlled/Related/Affected'}</th>
              <th style={{ width: '40%' }}>{isZh ? '說明' : 'Justification/Explanation'}</th>
              <th style={{ width: '10%' }}>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {currentEmissions.map((item, srcIndex) => (
              <React.Fragment key={srcIndex}>
                {item.ghg.map((g, ghgIndex) => (
                  <tr key={`${srcIndex}-${ghgIndex}`}>
                    {ghgIndex === 0 && (
                      <td rowSpan={item.ghg.length} style={{ verticalAlign: 'middle', fontWeight: 600 }}>
                        {item.source}
                      </td>
                    )}
                    <td>{getGasName(g.gas_id)}</td>
                    <td>
                      <span className={`status-badge ${g.is_included ? 'active' : 'inactive'}`}>
                        {g.is_included ? (isZh ? '包含' : 'Included') : (isZh ? '不包含' : 'Not included')}
                      </span>
                    </td>
                    <td>{getRelationshipLabel(g.relationship || '')}</td>
                    <td dangerouslySetInnerHTML={{ __html: g.justification || '-' }} />
                    {ghgIndex === 0 && (
                      <td rowSpan={item.ghg.length} className="actions" style={{ verticalAlign: 'middle' }}>
                        {canEdit && (
                          <>
                            <button className="btn-edit" onClick={() => openModal(srcIndex)} style={{ marginRight: '8px' }}>
                              {t('common.edit')}
                            </button>
                            <button className="btn-delete" onClick={() => handleDeleteSource(srcIndex)}>
                              {t('common.delete')}
                            </button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ========== 渲染 ==========
  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="nsection_b8" />
      </div>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

      {!selectedProposalId ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
          {isZh ? '請先在上方選擇方法論提案' : 'Please select a methodology proposal from the top bar'}
        </div>
      ) : (
        <div>
          {/* 頁籤 */}
          <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: '16px' }}>
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '10px 20px', border: 'none',
                  borderBottom: activeTab === tab.key ? '3px solid #4A90E2' : '3px solid transparent',
                  background: activeTab === tab.key ? '#f0f7ff' : 'transparent',
                  color: activeTab === tab.key ? '#4A90E2' : '#666',
                  fontWeight: activeTab === tab.key ? 600 : 400,
                  cursor: 'pointer', fontSize: '14px', marginBottom: '-2px'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : (
            <>
              {/* 頁籤1：邊界說明（富文本） */}
              {activeTab === 1 && (
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                    {isZh ? '邊界說明' : 'Boundary description'}
                  </label>
                  <RichTextEditor
                    value={boundaryDescription}
                    onChange={(html) => setBoundaryDescription(html)}
                    placeholder={isZh ? '請輸入活動邊界說明...' : 'Please enter activity boundary description...'}
                    disabled={!canEdit}
                  />
                </div>
              )}

              {/* 頁籤2/3/4：排放源 CRUD */}
              {(activeTab === 2 || activeTab === 3 || activeTab === 4) && (
                <div>
                  {canEdit && (
                    <div style={{ marginBottom: '12px', textAlign: 'right' }}>
                      <button className="btn-primary" onClick={() => openModal()}>
                        {t('common.create')}
                      </button>
                    </div>
                  )}
                  {renderEmissionsTable()}
                </div>
              )}

              {/* 儲存/重置按鈕 */}
              {canEdit && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '24px' }}>
                  <button className="btn-primary" onClick={handleSave} disabled={loading}>
                    {loading ? t('message.saving') : t('common.save')}
                  </button>
                  <button className="btn-secondary" onClick={handleReset} disabled={loading}>
                    {t('common.reset')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* 新增/編輯排放源 Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '950px' }}>
            <div className="modal-header">
              <h2>
                {editingIndex !== null
                  ? (isZh ? '編輯排放源' : 'Edit Source')
                  : (isZh ? '新增排放源' : 'Add Source')}
              </h2>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={handleModalSubmit}>
              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {/* 排放源名稱：文字框整合下拉（combo box） */}
                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <label style={{ fontWeight: 600 }}>
                    {isZh ? '排放源/碳庫名稱' : 'Source/sink name'} *
                  </label>
                  <input
                    type="text"
                    list="b8-source-options"
                    value={modalSource}
                    onChange={(e) => setModalSource(e.target.value)}
                    placeholder={isZh ? '選擇或輸入排放源名稱' : 'Select or enter source name'}
                    required
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '4px' }}
                  />
                  <datalist id="b8-source-options">
                    {Array.from(new Set([
                      ...baselineEmissions.map(e => e.source),
                      ...activityEmissions.map(e => e.source),
                      ...leakageEmissions.map(e => e.source)
                    ])).filter(s => s).map((s, i) => (
                      <option key={i} value={s} />
                    ))}
                  </datalist>
                </div>

                {/* 溫室氣體設定 */}
                <div style={{ marginBottom: '8px', fontWeight: 600 }}>
                  {isZh ? '溫室氣體設定' : 'GHG Settings'}
                </div>
                <table className="data-table" style={{ marginBottom: '16px' }}>
                  <thead>
                    <tr style={{ background: '#1a3a5c', color: '#fff' }}>
                      <th style={{ width: '4%', color: '#fff' }}></th>
                      <th style={{ width: '10%', color: '#fff' }}>{isZh ? '溫室氣體' : 'GHG'}</th>
                      <th style={{ width: '16%', color: '#fff' }}>{isZh ? '是否包含' : 'Included'}</th>
                      <th style={{ width: '22%', color: '#fff' }}>{isZh ? '受控/相關/受影響' : 'Controlled/Related/Affected'}</th>
                      <th style={{ width: '48%', color: '#fff' }}>{isZh ? '說明' : 'Justification/Explanation'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalGhg.map((g, idx) => (
                      <tr key={idx} style={{ opacity: (g as any)._selected === false ? 0.5 : 1 }}>
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={(g as any)._selected !== false}
                            onChange={(e) => updateModalGhg(idx, { _selected: e.target.checked } as any)}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                        </td>
                        <td>{getGasName(g.gas_id)}</td>
                        {/* 包含開關 */}
                        <td>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <div
                              onClick={() => updateModalGhg(idx, { is_included: !g.is_included })}
                              style={{
                                width: '44px', height: '24px', borderRadius: '12px',
                                background: g.is_included ? '#4CAF50' : '#ccc',
                                position: 'relative', cursor: 'pointer', transition: 'background 0.2s'
                              }}
                            >
                              <div style={{
                                width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
                                position: 'absolute', top: '2px',
                                left: g.is_included ? '22px' : '2px',
                                transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                              }} />
                            </div>
                            <span style={{ fontSize: '12px', color: g.is_included ? '#4CAF50' : '#999' }}>
                              {g.is_included ? (isZh ? '包含' : 'Included') : (isZh ? '不包含' : 'Not included')}
                            </span>
                          </label>
                        </td>
                        {/* Relationship Radio */}
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {RELATIONSHIP_OPTIONS.map(opt => (
                              <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '13px' }}>
                                <input
                                  type="radio"
                                  name={`relationship-${idx}`}
                                  checked={g.relationship === opt}
                                  onChange={() => updateModalGhg(idx, { relationship: opt })}
                                />
                                {getRelationshipLabel(opt)}
                              </label>
                            ))}
                          </div>
                        </td>
                        {/* 富文本欄位 */}
                        <td>
                          <RichTextEditor
                            value={g.justification}
                            onChange={(html) => updateModalGhg(idx, { justification: html })}
                            placeholder={isZh ? '說明/理由...' : 'Justification/Explanation...'}
                            minHeight="72px"
                            toolbar="inline"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary">{t('common.save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NsectionB8Page;
